<script setup lang="ts">
import { currentParty, mediaKindOf, mediaTitle } from '../lib/party'
import { can } from '../lib/roles'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { SpaceInfo } from '../api/types'
import { displayName, dmTitle } from '../lib/format'
import { shortCode } from '../lib/pattern'
import { describeError } from '../api/http'
import { useParty } from '../stores/party'
import { usePlayer } from '../stores/player'
import { useSession } from '../stores/session'
import { useUnread } from '../stores/unread'
import Avatar from './Avatar.vue'
import ChannelThreads from './ChannelThreads.vue'
import Icon from './Icon.vue'
import InviteModal from './InviteModal.vue'
import PromptModal from './PromptModal.vue'
import Modal from './Modal.vue'
import PeoplePicker from './PeoplePicker.vue'
import { useSectionNav } from '../lib/sectionNav'
import SpaceSettingsModal from './SpaceSettingsModal.vue'

const props = defineProps<{ space: SpaceInfo | null; dmMode: boolean; activeConv: string | null }>()
const session = useSession()
const unread = useUnread()
const router = useRouter()
const party = useParty()
const player = usePlayer()

const inviting = ref(false)
const creatingChannel = ref(false)
const startingDm = ref(false)
/** This space's settings (?space-settings=… in the URL). */
const spaceSettings = useSectionNav('space-settings')
/** What a channel's running party is playing, from its (decrypted) messages. */
function partyLabel(conv: string): { glyph: string; verb: string; title: string } {
  const p = currentParty(session.messages[conv] ?? [])
  const now = p?.party.queue[p.party.index]
  if (!p) return { glyph: '▶', verb: 'Party', title: '' }
  return mediaKindOf(now) === 'audio'
    ? { glyph: '∿', verb: 'Listening', title: mediaTitle(now) }
    : { glyph: '▶', verb: 'Watching', title: mediaTitle(now) }
}

/** Opens the channel and joins its running party (there's no card in the chat to join from). */
function joinParty(spaceId: string, conv: string): void {
  void router.push({ name: 'space', params: { spaceId, convId: conv } })
  const p = currentParty(session.messages[conv] ?? [])
  if (!p || party.joined?.id === p.party.id) return
  player.stop()
  party.join(conv, p.party.id)
}

// Who's in each channel's call or party, for the space being shown (frames keep it fresh).
watch(
  () => props.space?.id,
  () => {
    for (const c of props.space?.channels ?? []) {
      void session.loadPartyParticipants(c.id)
      void session.loadCallParticipants(c.id)
    }
  },
  { immediate: true },
)

const canManageChannels = computed(() => can(props.space, 'manage_channels'))
const canInvite = computed(() => can(props.space, 'invite_members'))
const dmList = computed(() =>
  session.dms.map((c) => {
    const others = c.members.filter((m) => m !== session.me)
    return { id: c.id, label: dmTitle(session.profiles, c.members, session.me, c.name), others, avatarId: others[0] ?? c.id }
  }),
)

// People you can DM: everyone you share a space with. Pick one, or several for a group.
const groupOf = (n: number) => (n > 1 ? `Group of ${n + 1}` : '')

async function createChannel(name: string): Promise<void> {
  if (!props.space) return
  const id = await session.createChannel(props.space.id, name.toLowerCase().replace(/\s+/g, '-'))
  await router.push({ name: 'space', params: { spaceId: props.space.id, convId: id } })
}

async function startDm(users: string[]): Promise<void> {
  try {
    const id = await session.createDm(users)
    startingDm.value = false
    await router.push({ name: 'dm', params: { convId: id } })
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}
</script>

<template>
  <aside class="flex h-full w-64 min-w-0 flex-col border-r border-border bg-bg-secondary max-md:w-auto" aria-label="Conversations">
    <!-- Space mode -->
    <template v-if="space && !dmMode">
      <!-- Space settings are the gear in the space's header, like a conversation's (HRTH-150). -->
      <div class="flex h-14 items-center justify-between gap-2 border-b border-border px-4 max-md:h-16">
        <span class="min-w-0">
          <span class="block truncate text-[15px] font-medium" data-testid="space-name">{{ space.name }}</span>
          <span class="mono block truncate text-text-muted">
            <template v-if="space.host"><span data-testid="space-host">@{{ space.host }}</span> · </template>{{ space.members.length }} {{ space.members.length === 1 ? 'member' : 'members' }} · {{ shortCode(space.id) }}
          </span>
        </span>
        <button class="btn-icon" title="Space settings" aria-label="Space settings" data-testid="space-settings" @click="spaceSettings.open()">
          <Icon name="gear" />
        </button>
      </div>
      <div class="scroll-thin flex-1 overflow-y-auto py-3">
        <button
          v-if="canInvite"
          class="btn-ghost mx-4 mb-4 w-[calc(100%-2rem)] justify-start border-dashed border-border"
          data-testid="invite-button"
          @click="inviting = true"
        >
          + Invite friends
        </button>
        <div class="flex items-center justify-between px-4 pb-2">
          <span class="label mb-0">Channels</span>
          <button
            v-if="canManageChannels"
            class="btn-icon"
            title="Create channel"
            aria-label="Create channel"
            data-testid="create-channel"
            @click="creatingChannel = true"
          >
            <Icon name="plus" />
          </button>
        </div>
        <template v-for="c in space.channels" :key="c.id">
          <button
            class="relative flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm transition-colors"
            :class="c.id === activeConv ? 'bg-bg-hover text-text-primary' : 'text-text-muted hover:text-text-primary'"
            :data-testid="`channel-${c.name}`"
            @click="router.push({ name: 'space', params: { spaceId: space.id, convId: c.id } })"
          >
            <span v-if="c.id === activeConv" class="absolute inset-y-0 left-0 w-0.5 bg-ink" />
            <span class="mono text-text-disabled">#</span>
            <span class="truncate" :class="unread.counts[c.id] && c.id !== activeConv ? 'text-text-primary' : ''">{{ c.name }}</span>
            <span v-if="unread.counts[c.id] && c.id !== activeConv" class="badge ml-auto" :data-testid="`unread-${c.name}`">{{ unread.counts[c.id] }}</span>
          </button>
          <ChannelThreads :space-id="space.id" :conv="c.id" :name="c.name ?? ''" />
          <div
            v-if="session.inCall[c.id]?.length"
            class="mb-1 ml-8 flex flex-col gap-1 py-1"
            :data-testid="`in-call-${c.name}`"
            :title="`In the call: ${session.inCall[c.id]!.map((u) => displayName(session.profiles, u)).join(', ')}`"
          >
            <span class="mono flex items-center gap-1.5 text-text-muted"><span class="h-1.5 w-1.5 animate-blink bg-signal" />LIVE</span>
            <span v-for="u in session.inCall[c.id]" :key="u" class="inline-flex items-center gap-2 text-xs">
              <Avatar :id="u" :name="displayName(session.profiles, u)" :size="16" />
              <span class="text-text-secondary">{{ displayName(session.profiles, u) }}</span>
            </span>
          </div>
          <!-- Watch party / listening room: what's playing (from encrypted messages) and who's in it -->
          <button
            v-if="session.inParty[c.id]?.length"
            class="mb-1 ml-8 flex w-[calc(100%-2.5rem)] flex-col gap-1 py-1 text-left"
            :data-testid="`in-party-${c.name}`"
            :title="`${partyLabel(c.id).verb}: ${session.inParty[c.id]!.map((u) => displayName(session.profiles, u)).join(', ')}`"
            @click="joinParty(space.id, c.id)"
          >
            <span class="mono flex min-w-0 items-center gap-1.5 text-text-muted">
              <span class="shrink-0 text-voice">{{ partyLabel(c.id).glyph }}</span>
              <span class="shrink-0 uppercase">{{ partyLabel(c.id).verb }}</span>
              <span v-if="partyLabel(c.id).title" class="truncate normal-case text-text-secondary" data-testid="party-now">· {{ partyLabel(c.id).title }}</span>
              <span v-if="party.joined?.conv !== c.id" class="ml-auto shrink-0 pr-1 uppercase text-text-primary">Join</span>
            </span>
            <span v-for="u in session.inParty[c.id]" :key="u" class="inline-flex items-center gap-2 text-xs">
              <Avatar :id="u" :name="displayName(session.profiles, u)" :size="16" />
              <span class="text-text-secondary">{{ displayName(session.profiles, u) }}</span>
            </span>
          </button>
        </template>
      </div>
    </template>

    <!-- DM mode -->
    <template v-else-if="dmMode">
      <div class="flex h-14 items-center justify-between border-b border-border px-4 max-md:h-16">
        <span>
          <span class="block text-[15px] font-medium">Direct messages</span>
          <span class="mono block text-text-muted">{{ dmList.length }} conversations · e2ee</span>
        </span>
        <button class="btn-icon" title="New message" aria-label="New message" data-testid="new-dm" @click="startingDm = true">
          <Icon name="plus" />
        </button>
      </div>
      <div class="scroll-thin flex-1 overflow-y-auto py-2">
        <p v-if="dmList.length === 0" class="mono px-4 py-4 text-text-muted">No conversations yet.</p>
        <button
          v-for="d in dmList"
          :key="d.id"
          class="relative flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
          :class="d.id === activeConv ? 'bg-bg-hover text-text-primary' : 'text-text-muted hover:text-text-primary'"
          :data-testid="`dm-${d.label}`"
          @click="router.push({ name: 'dm', params: { convId: d.id } })"
        >
          <span v-if="d.id === activeConv" class="absolute inset-y-0 left-0 w-0.5 bg-ink" />
          <!-- A group shows two of its people, overlapped -->
          <span v-if="d.others.length > 1" class="relative h-6 w-6 shrink-0" data-testid="dm-group-avatar">
            <Avatar :id="d.others[0]!" :name="d.label" :size="16" class="absolute top-0 left-0" />
            <Avatar :id="d.others[1]!" :name="d.label" :size="16" class="absolute right-0 bottom-0 ring-2 ring-bg-secondary" />
          </span>
          <Avatar v-else :id="d.avatarId" :name="d.label" :size="24" presence />
          <span class="truncate" :class="unread.counts[d.id] && d.id !== activeConv ? 'text-text-primary' : ''">{{ d.label }}</span>
          <span v-if="unread.counts[d.id] && d.id !== activeConv" class="badge ml-auto">{{ unread.counts[d.id] }}</span>
        </button>
      </div>
    </template>
    <div v-else class="flex-1" />

    <SpaceSettingsModal v-if="spaceSettings.at.value && space" :space="space" />
    <InviteModal v-if="inviting && space" :space="space" @close="inviting = false" />
    <PromptModal
      v-if="creatingChannel"
      title="Create channel"
      label="Channel name"
      placeholder="e.g. music"
      submit-label="Create"
      :maxlength="48"
      :action="createChannel"
      @close="creatingChannel = false"
    />
    <Modal v-if="startingDm" title="New message" @close="startingDm = false">
      <!-- 15: the most a group holds (16) besides you -->
      <PeoplePicker :max="15" submit-label="Message" :summary="groupOf" @pick="startDm" />
    </Modal>
  </aside>
</template>
